// EditableShape adapter for Zone objects
// Translates uniform tool interface into zoneState mutations
// Supports undo via beginEdit/commit/cancel snapshot flow

import type { EditableShape } from '@/types/editor'
import type { Zone } from '@/types/zone'
import { zoneState } from '@/zones/ZoneState'
import { getZoneTypeDef } from '@/constants/zoneTypes'
import { generatePathPolygon } from '@/utils/pathGeometry'
import { undoStack } from '../UndoStack'
import { debug } from '@/utils/debug'

export class ZoneAdapter implements EditableShape {
  readonly sourceType = 'zone' as const
  private zoneId: string

  /** Snapshot taken at beginEdit(), used to compute undo delta */
  private snapshot: { x: number; y: number; width: number; height: number; rotation: number; points?: Array<{ x: number; y: number }>; pathWaypoints?: Array<{ x: number; y: number }>; pathWidth?: number } | null = null

  constructor(zone: Zone) {
    this.zoneId = zone.id
  }

  get id(): string {
    return this.zoneId
  }

  private getZone(): Zone {
    const zone = zoneState.getZoneById(this.zoneId)
    if (!zone) throw new Error(`ZoneAdapter: zone ${this.zoneId} not found`)
    return zone
  }

  // --- Read geometry (editor coords, dots) ---

  getCenter(): { x: number; y: number } {
    const z = this.getZone()
    return { x: z.x, y: z.y }
  }

  getSize(): { width: number; height: number } {
    const z = this.getZone()
    return { width: z.width, height: z.height }
  }

  getRotation(): number {
    return this.getZone().rotation
  }

  getShapeKind(): 'rectangle' | 'ellipse' | 'polygon' {
    const z = this.getZone()
    if (z.points && z.points.length >= 3) return 'polygon'
    if (z.shapeKind === 'path') return 'polygon'
    return z.shapeKind === 'ellipse' ? 'ellipse' : 'rectangle'
  }

  getPoints(): Array<{ x: number; y: number }> | null {
    const z = this.getZone()
    // For path zones, edit the waypoints (not the outline)
    if (z.shapeKind === 'path' && z.pathWaypoints) return z.pathWaypoints
    return z.points ?? null
  }

  isOpenPath(): boolean {
    return this.getZone().shapeKind === 'path'
  }

  // --- Write geometry (editor coords, dots) ---

  moveTo(x: number, y: number): void {
    zoneState.updateZone(this.zoneId, { x, y })
  }

  resize(width: number, height: number): void {
    zoneState.updateZone(this.zoneId, { width, height })
  }

  rotate(degrees: number): void {
    zoneState.updateZone(this.zoneId, { rotation: degrees })
  }

  setPoints(points: Array<{ x: number; y: number }>): void {
    const z = this.getZone()
    if (z.shapeKind === 'path' && z.pathWidth) {
      // Regenerate outline from updated waypoints
      const outline = generatePathPolygon(points, z.pathWidth)
      const xs = outline.map(p => p.x)
      const ys = outline.map(p => p.y)
      zoneState.updateZone(this.zoneId, {
        pathWaypoints: points,
        points: outline,
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys),
      })
      return
    }
    zoneState.updateZone(this.zoneId, { points })
  }

  // --- Visual ---

  getAccentColor(): number {
    return getZoneTypeDef(this.getZone().zoneType).color
  }

  // --- Undo integration ---

  beginEdit(): void {
    const z = this.getZone()
    this.snapshot = {
      x: z.x, y: z.y, width: z.width, height: z.height, rotation: z.rotation,
      points: z.points ? z.points.map(p => ({ ...p })) : undefined,
      pathWaypoints: z.pathWaypoints ? z.pathWaypoints.map(p => ({ ...p })) : undefined,
      pathWidth: z.pathWidth,
    }
  }

  commit(): void {
    if (!this.snapshot) return // No beginEdit was called, nothing to undo

    const before = this.snapshot
    const z = this.getZone()
    const after = {
      x: z.x, y: z.y, width: z.width, height: z.height, rotation: z.rotation,
      points: z.points ? z.points.map(p => ({ ...p })) : undefined,
      pathWaypoints: z.pathWaypoints ? z.pathWaypoints.map(p => ({ ...p })) : undefined,
      pathWidth: z.pathWidth,
    }

    // Skip if nothing changed
    const pointsChanged = JSON.stringify(before.points) !== JSON.stringify(after.points)
    if (before.x === after.x && before.y === after.y &&
        before.width === after.width && before.height === after.height &&
        before.rotation === after.rotation && !pointsChanged) {
      this.snapshot = null
      return
    }

    const id = this.zoneId
    undoStack.push({
      description: `Edit ${id}`,
      undo: () => zoneState.updateZone(id, before),
      redo: () => zoneState.updateZone(id, after),
    })

    debug.log('ZoneAdapter', `Committed edit for ${id}`)
    this.snapshot = null
  }

  cancel(): void {
    if (!this.snapshot) return
    zoneState.updateZone(this.zoneId, this.snapshot)
    debug.log('ZoneAdapter', `Cancelled edit for ${this.zoneId}`)
    this.snapshot = null
  }
}
